import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import SimpleHeader from './SimpleHeader';
import Footer from './Footer';

// Backend API URL - configurable per environment
const BACKEND_URL =
  process.env.REACT_APP_PUBLIC_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://app.getphotoshoot.ai'
    : 'http://localhost:6779');

// Payment Status Page - handles PhonePe redirect (both GET and POST)
// PhonePe Standard Checkout uses POST redirect mode
const PaymentStatusPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const formSubmittedRef = useRef(false);
  
  // Parse query parameters (for GET redirects)
  const getQueryParams = useCallback(() => {
    const params = new URLSearchParams(location.search);
    // PhonePe OAuth API redirects with merchantOrderId or orderId
    return {
      transactionId: params.get('transactionId') || 
                     params.get('merchantTransactionId') || 
                     params.get('merchantOrderId') ||
                     params.get('orderId'),
      code: params.get('code'),
      state: params.get('state')
    };
  }, [location.search]);
  
  // Parse POST data (for POST redirects from PhonePe)
  const getPostData = useCallback(() => {
    // PhonePe POST redirect sends data in the URL hash or as form data
    // Check if we have POST data in location state (from form submission)
    if (location.state && location.state.phonepeResponse) {
      return location.state.phonepeResponse;
    }
    return null;
  }, [location.state]);
  
  const [status, setStatus] = useState('checking');
  const [transactionId, setTransactionId] = useState(null);
  const [transactionDetails, setTransactionDetails] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(null);
  const pollingStoppedRef = useRef(false);

  // Handle PhonePe POST redirect - PhonePe posts form data to redirectUrl
  useEffect(() => {
    // Check if this is a POST redirect from PhonePe
    // PhonePe POST redirect sends data as form fields
    const handlePostRedirect = () => {
      // PhonePe may send data in different ways:
      // 1. As URL hash parameters
      // 2. As form data (if page was loaded via POST)
      // 3. As query parameters (fallback)
      
      // Try to get transaction ID from various sources
      let txId = null;
      let code = null;
      
      // console.log('🔍 Parsing PhonePe redirect:', {
      //   fullUrl: window.location.href,
      //   hash: window.location.hash,
      //   search: window.location.search,
      //   pathname: window.location.pathname
      // });
      
      // Check URL hash (PhonePe sometimes uses this)
      if (window.location.hash) {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        txId = hashParams.get('merchantTransactionId') || 
               hashParams.get('transactionId') ||
               hashParams.get('merchantOrderId') ||
               hashParams.get('orderId');
        code = hashParams.get('code');
       // console.log('📋 Hash params:', { txId, code });
      }
      
      // Check query parameters
      if (!txId) {
        const queryParams = getQueryParams();
        txId = queryParams.transactionId;
        code = queryParams.code || queryParams.state;
       // console.log('📋 Query params:', { txId, code, allParams: Object.fromEntries(new URLSearchParams(location.search)) });
      }
      
      // Check POST data from location state
      const postData = getPostData();
      if (postData) {
        txId = postData.merchantTransactionId || 
               postData.transactionId || 
               postData.merchantOrderId ||
               postData.orderId ||
               txId;
        code = postData.code || postData.state || code;
       // console.log('📋 POST data:', { txId, code, postData });
      }
      
      // PhonePe Standard Checkout might redirect with merchantOrderId in query
      // Check all possible parameter names
      const allParams = new URLSearchParams(location.search);
      if (!txId) {
        txId = allParams.get('merchantOrderId') || 
               allParams.get('merchantTransactionId') ||
               allParams.get('transactionId') ||
               allParams.get('orderId') ||
               allParams.get('txnId');
      }
      
      return { transactionId: txId, code };
    };
    
    const { transactionId: txId, code } = handlePostRedirect();
    
    if (txId) {
     // console.log('✅ Transaction ID found:', txId);
      setTransactionId(txId);
      
      // Remove transactionId from URL to keep it clean (use replace to avoid adding to history)
      if (window.location.search.includes('transactionId=')) {
        const url = new URL(window.location.href);
        url.searchParams.delete('transactionId');
        window.history.replaceState({}, '', url.pathname + (url.search || ''));
       // console.log('🧹 Cleaned transactionId from URL');
      }
      
      // If we have a code, we can determine status immediately
      if (code === 'PAYMENT_SUCCESS' || code === 'SUCCESS') {
        setStatus('success');
      } else if (code === 'PAYMENT_ERROR' || code === 'PAYMENT_FAILURE') {
        setStatus('failed');
      }
    } else {
      console.warn('⚠️ No transaction ID found in redirect URL. PhonePe may not be including it.');
     // console.log('📋 Will check most recent pending payment instead');
      // Set a special marker to indicate we should check recent payment
      setTransactionId('RECENT_PENDING');
    }
  }, [getQueryParams, getPostData, location.search]);

  // Poll for payment status from backend
  useEffect(() => {
    if (!transactionId) {
      return;
    }

    // Poll for payment status
    const checkPaymentStatus = async () => {
      // Stop polling if already stopped or if status is final (success/failed/error)
      if (pollingStoppedRef.current || status === 'success' || status === 'failed' || status === 'error') {
        return;
      }

      try {
        const token = localStorage.getItem('authToken');
        if (!token) {
          setStatus('error');
          pollingStoppedRef.current = true;
          return;
        }

        // If transactionId is 'RECENT_PENDING', make request without transaction ID
        // Backend will use most recent pending payment
        const statusUrl = transactionId === 'RECENT_PENDING' 
          ? `${BACKEND_URL}/api/payment/status`
          : `${BACKEND_URL}/api/payment/status/${transactionId}`;

        // console.log('📤📤📤 SENDING PAYMENT STATUS CHECK REQUEST 📤📤📤');
        // console.log('='.repeat(80));
        // console.log('URL:', statusUrl);
        // console.log('Transaction ID:', transactionId === 'RECENT_PENDING' ? 'MOST_RECENT_PENDING' : transactionId);
        // console.log('Timestamp:', new Date().toISOString());
        // console.log('Has Auth Token:', !!localStorage.getItem('authToken'));
        // console.log('='.repeat(80));

        const response = await axios.get(
          statusUrl,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        // console.log('📥📥📥 PAYMENT STATUS CHECK RESPONSE - FULL DATA 📥📥📥');
        // console.log('='.repeat(80));
        // console.log('Status Code:', response.status);
        // console.log('Status Text:', response.statusText);
        // console.log('Response Headers:', JSON.stringify(response.headers, null, 2));
        // console.log('Response Data:', JSON.stringify(response.data, null, 2));
        // console.log('Full Response Object:', response);
        // console.log('='.repeat(80));
        // console.log('📥📥📥 END OF PAYMENT STATUS RESPONSE 📥📥📥');

        if (response.data?.success) {
          const paymentStatus = response.data.data.status || response.data.data.ourTransaction?.status;
          const details = response.data.data;
          // console.log('✅✅✅ PAYMENT STATUS SUCCESS ✅✅✅');
          // console.log('='.repeat(80));
          // console.log('Payment Status:', paymentStatus);
          // console.log('Full Details:', JSON.stringify(details, null, 2));
          // console.log('Credits:', details?.credits || 'N/A');
          // console.log('Amount:', details?.amount || 'N/A');
          // console.log('PhonePe Response:', details?.phonepeResponse || 'N/A');
          // console.log('='.repeat(80));
          setStatus(paymentStatus);
          setTransactionDetails(details); // Store transaction details for display

          if (paymentStatus === 'success') {
            pollingStoppedRef.current = true; // Stop polling
            setStatus('success'); // Ensure status is set to success
            toast.success(`Payment successful! ${response.data.data.credits || ''} credits added to your wallet.`);
            // Refresh user data to show updated credits
            window.dispatchEvent(new Event('refreshUserData'));
            // Redirect after 5-7 seconds to show success message and transaction details
            const redirectDelay = 5000 + Math.random() * 2000; // Random between 5-7 seconds (5000-7000ms)
            const seconds = Math.ceil(redirectDelay / 1000);
            
            // Start countdown
            setRedirectCountdown(seconds);
            const countdownInterval = setInterval(() => {
              setRedirectCountdown(prev => {
                if (prev <= 1) {
                  clearInterval(countdownInterval);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
            
            setTimeout(() => {
              clearInterval(countdownInterval);
              // Force full page reload to ensure homepage is displayed
              // Using window.location.href ensures a complete page reload and route change
              window.location.href = '/';
            }, redirectDelay);
          } else if (paymentStatus === 'failed') {
            pollingStoppedRef.current = true; // Stop polling
            setStatus('failed'); // Ensure status is set to failed
            toast.error('Payment failed. Please try again.');
            setTimeout(() => {
              // Force full page reload to ensure homepage is displayed
              window.location.href = '/';
            }, 5000);
          } else if (paymentStatus === 'pending') {
            // Keep checking if still pending
            setStatus('checking');
          }
        } else {
          console.error('❌ Payment status check failed:', response.data);
          const errorMsg = response.data?.error || response.data?.debug?.message || 'Unable to verify payment';
          toast.error(errorMsg);
          pollingStoppedRef.current = true; // Stop polling on error
          setStatus('error');
        }
      } catch (error) {
        console.error('❌❌❌ PAYMENT STATUS CHECK ERROR ❌❌❌');
        console.error('='.repeat(80));
        console.error('Error Message:', error.message);
        console.error('Error Stack:', error.stack);
        if (error.response) {
          console.error('❌ Error Response Details:');
          console.error('Status Code:', error.response.status);
          console.error('Status Text:', error.response.statusText);
          console.error('Response Headers:', JSON.stringify(error.response.headers, null, 2));
          console.error('Response Data:', JSON.stringify(error.response.data, null, 2));
          console.error('Full Error Response:', {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers
          });
          
          if (error.response.status === 404) {
            const errorData = error.response.data;
            const debugInfo = errorData?.debug;
            if (debugInfo) {
              console.error('❌ Debug info:', debugInfo);
              toast.error(debugInfo.message || 'Payment not found. ' + (debugInfo.hint || ''));
            } else {
              toast.error('Payment not found. Please check your payment history.');
            }
            pollingStoppedRef.current = true; // Stop polling on 404
            setStatus('error');
            return;
          }
        }
        
        // Don't set error immediately, keep checking (webhook might still process)
        if (status === 'checking') {
          // Only set error if we've been checking for a while
          setTimeout(() => {
            if (status === 'checking' && !pollingStoppedRef.current) {
              pollingStoppedRef.current = true; // Stop polling after timeout
              setStatus('error');
              toast.error('Unable to verify payment. Please check your wallet or contact support.');
            }
          }, 10000); // Wait 10 seconds before showing error
        } else {
          pollingStoppedRef.current = true; // Stop polling on other errors
          setStatus('error');
        }
      }
    };

    // Log the complete redirect information
    console.log('📋 Complete Redirect Information:', {
      fullUrl: window.location.href,
      origin: window.location.origin,
      pathname: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      allQueryParams: Object.fromEntries(new URLSearchParams(location.search)),
      allHashParams: window.location.hash ? Object.fromEntries(new URLSearchParams(window.location.hash.substring(1))) : null,
      locationState: location.state,
      transactionId: transactionId
    });

    // Reset polling stopped flag when transactionId changes
    pollingStoppedRef.current = false;

    // Initial check
    checkPaymentStatus();
    
    // Poll every 2 seconds (webhook should process quickly, but polling ensures UI updates)
    // Only poll if status is still checking/pending
    const interval = setInterval(() => {
      // Check if we should stop polling
      if (pollingStoppedRef.current || status === 'success' || status === 'failed' || status === 'error') {
        clearInterval(interval);
        return;
      }
      checkPaymentStatus();
    }, 2000);
    
    // Stop polling after 30 seconds (webhook should have processed by then)
    const timeout = setTimeout(() => {
      if (!pollingStoppedRef.current) {
        pollingStoppedRef.current = true;
        clearInterval(interval);
        if (status === 'checking') {
          // If still checking after 30 seconds, show error
          setStatus('error');
        }
      }
    }, 30000);
    
    return () => {
      pollingStoppedRef.current = true; // Mark as stopped on cleanup
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [transactionId, navigate]);

  return (
    <>
      <SimpleHeader />
      <div className="payment-status-page">
        <div className="payment-status-container">
          {status === 'checking' && (
            <>
              <div className="payment-status-icon">⏳</div>
              <h1>Verifying Payment...</h1>
              <p>Please wait while we verify your payment.</p>
            </>
          )}
          {status === 'success' && (
            <>
              <div className="payment-status-icon success">✅</div>
              <h1>Payment Successful!</h1>
              <p>Your credits have been added to your wallet.</p>
              {transactionDetails && (
                <div className="transaction-details" style={{ marginTop: '20px', textAlign: 'left', maxWidth: '500px', margin: '20px auto', padding: '15px', background: '#f5f5f5', borderRadius: '8px' }}>
                  <h3 style={{ marginBottom: '15px', fontSize: '16px' }}>Transaction Details</h3>
                  
                  {transactionDetails.ourTransaction && (
                    <div style={{ marginBottom: '15px' }}>
                      <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>Our Transaction:</h4>
                      <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                        <p><strong>Transaction ID:</strong> {transactionDetails.ourTransaction.transactionId}</p>
                        <p><strong>Order ID:</strong> {transactionDetails.ourTransaction.orderId}</p>
                        <p><strong>Credits:</strong> {transactionDetails.ourTransaction.credits}</p>
                        <p><strong>Amount:</strong> ₹{transactionDetails.ourTransaction.amount || '0.00'}</p>
                        <p><strong>Status:</strong> {transactionDetails.ourTransaction.status}</p>
                      </div>
                    </div>
                  )}
                  
                  {transactionDetails.phonepeTransaction && (
                    <div>
                      <h4 style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>PhonePe Transaction:</h4>
                      <div style={{ fontSize: '12px', lineHeight: '1.6' }}>
                        <p><strong>PhonePe Order ID:</strong> {transactionDetails.phonepeTransaction.orderId || 'N/A'}</p>
                        <p><strong>State:</strong> {transactionDetails.phonepeTransaction.state || 'N/A'}</p>
                        <p><strong>Amount:</strong> ₹{transactionDetails.phonepeTransaction.amount ? (transactionDetails.phonepeTransaction.amount / 100).toFixed(2) : '0.00'}</p>
                        {transactionDetails.phonepeTransaction.paymentInstrument && (
                          <p><strong>Payment Method:</strong> {transactionDetails.phonepeTransaction.paymentInstrument.type || 'N/A'}</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
              <p style={{ marginTop: '15px', fontSize: '14px', color: '#666' }}>
                {redirectCountdown !== null && redirectCountdown > 0 ? (
                  <>Redirecting to home in {redirectCountdown} second{redirectCountdown !== 1 ? 's' : ''}...</>
                ) : (
                  <>Redirecting to home...</>
                )}
              </p>
            </>
          )}
          {status === 'failed' && (
            <>
              <div className="payment-status-icon error">❌</div>
              <h1>Payment Failed</h1>
              <p>Your payment could not be processed. Please try again.</p>
              <button className="payment-retry-btn" onClick={() => { window.location.href = '/'; }}>
                Go to Home
              </button>
            </>
          )}
          {status === 'error' && (
            <>
              <div className="payment-status-icon error">⚠️</div>
              <h1>Unable to Verify Payment</h1>
              <p>Please check your wallet or contact support if credits were not added.</p>
              <button className="payment-retry-btn" onClick={() => { window.location.href = '/'; }}>
                Go to Home
              </button>
            </>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default PaymentStatusPage;

